import { useEffect, useState } from 'react';

import useMe from '@/renderer/utils/useMe';
import useRequest from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';

// Persist the flag through the shared self-update endpoint (same path the other
// notification-preference toggles use). The server stores it on
// meta.supportPushMuted and honors it in directMessageReceipt/service.js.
const updateSupportPushMuteRequest: RequestSchema<
  API.User,
  API.WithData<{ supportPushMuted: boolean }>
> = {
  name: 'updateSupportPushMuteRequest',
  input: '/api/v1/user/update/me?v2Verify=true',
  method: 'POST',
};

/**
 * Per-staff support-chat push mute (#1439). Reads the current user's
 * `meta.supportPushMuted` and lets them flip it (e.g. while OOO). Muting only
 * silences that staffer's own inbound support-chat push — they still see
 * messages in-app, and the global quiet-hours + morning digest are untouched.
 *
 * The `/user/update/me` response omits `meta` (it returns only default columns),
 * so we merge the new value onto the shared `me` locally rather than trusting the
 * returned user — that keeps every mount (header button + settings switch) in
 * sync without a refetch. Optimistic, reverting on failure.
 */
export default function useSupportPushMute() {
  const [me, setMe] = useMe(true);
  const serverMuted = !!me?.meta?.supportPushMuted;
  const [muted, setLocalMuted] = useState(serverMuted);
  const request = useRequest(updateSupportPushMuteRequest, { manual: true });

  // Track the source of truth as `me` loads or changes elsewhere.
  useEffect(() => {
    setLocalMuted(serverMuted);
  }, [serverMuted]);

  const setMuted = async (next: boolean) => {
    setLocalMuted(next); // optimistic
    try {
      await request.run({ data: { supportPushMuted: next } }, { throwOnRun: true });
      if (me) {
        setMe({ ...me, meta: { ...(me.meta ?? {}), supportPushMuted: next } });
      }
    } catch (error) {
      setLocalMuted(serverMuted); // revert on failure
      throw error;
    }
  };

  return {
    muted,
    setMuted,
    toggle: () => setMuted(!muted),
    loading: request.loading,
  };
}
